import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { styled } from '@mui/system'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { communities } from '@api/swagger'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import useLoadingContext from '@hooks/useLoadingContext'
import { useLoadingIds } from '@hooks/useLoadingIds'
import { usePagination } from '@hooks/usePagination'
import { useShowOnDesktop } from '@hooks/useShowOnDesktop'
import {
  InvitationSource,
  PaginationResponse,
  PotentialInviteItem,
  PotentialInviteStatus,
  OrderByDirection,
} from '@swagger'
import { useCommunitiesContext } from '../Context/CommunitiesContext'
import DropDown, { DropDownVariant, MenuOption } from '../Menus/DropDown'
import SendInviteModal from '../Modals/SendInviteModal'
import SearchBar from '../Search/SearchBar'
import { ActionableTable } from '../Table/ActionableTable'
import {
  getAwaitingDecisionsTableColumns,
  getSourceLabel,
  getStatusLabel,
  AwaitingDecisionRow,
} from './getAwaitingDecisionsTableColumns'
import { getAwaitingDecisionsTableRowActions } from './getAwaitingDecisionsTableRowActions'
import FamilyDetailsModal from '../Modals/FamilyDetailsModal'
import PassPotentialInviteModal from '@components/Modals/PassPotentialInviteModal'

export const AWAITING_DECISIONS_MIN_SEARCH_LENGTH = 3

const ALL_SOURCES = 'AllSources'
const ALL_STATUSES = 'AllStatuses'

const toAwaitingDecisionRow = (
  invite: PotentialInviteItem
): AwaitingDecisionRow => ({
  id: invite.potentialInviteKey,
  familyName: invite.familyName,
  source: invite.source,
  contactEmail: invite.contactEmail,
  status: invite.status,
  crmEmails: invite.crmEmails,
  potentialInvite: invite,
  parentFirstName: invite.parentFirstName,
  parentLastName: invite.parentLastName,
  potentialInviteKey: invite.potentialInviteKey,
  parentUserKey: invite.parentUserKey,
  previousPrograms: invite.previousPrograms,
})

const SearchAndFilterWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}))

const FilterContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
  },
}))

const SortContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    margin: theme.spacing(0, 3),
  },
}))

const StyledSourceCountsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(4),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'row',
  },
}))

export const CommunityAwaitingDecisionsTable: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { handleError } = useAppSnackbar()
  const showOnDesktop = useShowOnDesktop()
  const { selectedCommunityKey, academicYear, communityName } =
    useCommunitiesContext()
  const [searchParams] = useSearchParams()
  // Set when coming from the dashboard for a specific LCD; restricts the list to that LCD's families.
  const actorKeyParam = Number(searchParams.get('actorKey'))
  const actorKey =
    Number.isInteger(actorKeyParam) && actorKeyParam > 0
      ? actorKeyParam
      : undefined
  const {
    AwaitingDecisionsTable: {
      fetchPotentialInvites: fetchAwaitingDecisionsLoadingId,
    },
  } = useLoadingIds()

  const [awaitingDecisionsData, setAwaitingDecisionsData] = useState<
    AwaitingDecisionRow[]
  >([])
  const [lcdReferralCount, setLcdReferralCount] = useState<number>()
  const [returningFamilyCount, setReturningFamilyCount] = useState<number>()
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)

  const { pagination, setPage, setPageSize, setOrderBy, setTotalCount } =
    usePagination({ initialOrderBy: [{ familyName: OrderByDirection.Asc }] })
  const [search, setSearch] = useState('')
  const effectiveSearch =
    search.length >= AWAITING_DECISIONS_MIN_SEARCH_LENGTH ? search : undefined
  const [sourceFilter, setSourceFilter] = useState<
    InvitationSource | undefined
  >(undefined)
  const statusParam = Object.values(PotentialInviteStatus).find(
    (status) => status === searchParams.get('status')
  )
  const [statusFilter, setStatusFilter] = useState<
    PotentialInviteStatus | undefined
  >(statusParam)

  const [modalState, setModalState] = useState<
    | { modalType: 'sendInvite'; row: AwaitingDecisionRow }
    | { modalType: 'familyDetails'; row: AwaitingDecisionRow }
    | { modalType: 'passPotentialInvite'; row: AwaitingDecisionRow }
    | null
  >(null)

  const handleOpenSendInviteModal = (row: AwaitingDecisionRow) => {
    setModalState({ modalType: 'sendInvite', row })
  }

  const handleOpenFamilyDetails = (row: AwaitingDecisionRow) => {
    setModalState({ modalType: 'familyDetails', row })
  }

  const handleOpenPassPotentialInviteModal = (row: AwaitingDecisionRow) => {
    setModalState({ modalType: 'passPotentialInvite', row })
  }

  const handleCloseModal = () => {
    setModalState(null)
  }

  // Guards against a stale response (from a request superseded by a newer
  // one, e.g. rapid filter changes under allowReentry) overwriting fresher
  // state.
  const latestRequestIdRef = useRef(0)

  const fetchAwaitingDecisions = async () => {
    if (!selectedCommunityKey || !academicYear) return

    const requestId = ++latestRequestIdRef.current

    try {
      const response = await communities.fetchPotentialInvites({
        communityKey: selectedCommunityKey,
        page: pagination.page,
        pageSize: pagination.pageSize,
        orderBy: pagination.orderBy?.length
          ? pagination.orderBy.map((clause) => {
              let field = Object.keys(clause)[0]
              const direction = Object.values(clause)[0]
              if (field === 'familyName') field = 'parentLastName'
              return `${field} ${direction}`
            })
          : undefined,
        academicYear,
        source: sourceFilter,
        status: statusFilter,
        search: effectiveSearch,
        actorKey,
      })

      // A newer request has since been triggered (e.g. the user changed a
      // filter again); ignore this now-stale response.
      if (latestRequestIdRef.current !== requestId) return

      setAwaitingDecisionsData(
        response.potentialInvites.map(toAwaitingDecisionRow)
      )

      setLcdReferralCount(response.lcdReferralCount)
      setReturningFamilyCount(response.returningFamilyCount)

      setTotalCount(
        response.pagination?.totalCount ?? response.potentialInvites.length
      )
    } catch (error) {
      if (latestRequestIdRef.current !== requestId) return
      await handleError(error)
    }
  }

  const { triggerFetch, isLoading: isFetchingAwaitingDecisions } =
    useLoadingContext({
      asyncFunction: fetchAwaitingDecisions,
      loadingId: fetchAwaitingDecisionsLoadingId,
      allowReentry: true,
    })

  useEffect(() => {
    if (isFetchingAwaitingDecisions) setHasFetchedOnce(true)
  }, [isFetchingAwaitingDecisions])
  const isLoading = !hasFetchedOnce || isFetchingAwaitingDecisions

  useEffect(() => {
    triggerFetch()
  }, [
    selectedCommunityKey,
    academicYear,
    effectiveSearch,
    sourceFilter,
    statusFilter,
    actorKey,
    pagination.page,
    pagination.pageSize,
    pagination.orderBy,
    triggerFetch,
  ])

  const handleSearch = (searchText: string) => {
    setPage(1)
    setSearch(searchText)
  }

  const sourceMenuOptions: MenuOption[] = [
    { id: ALL_SOURCES, name: getSourceLabel(t, undefined) },
    {
      id: InvitationSource.LcdReferral,
      name: getSourceLabel(t, InvitationSource.LcdReferral),
    },
    {
      id: InvitationSource.ReturningFamily,
      name: getSourceLabel(t, InvitationSource.ReturningFamily),
    },
  ]

  const statusMenuOptions: MenuOption[] = [
    { id: ALL_STATUSES, name: getStatusLabel(t, undefined) },
    {
      id: PotentialInviteStatus.Declined,
      name: getStatusLabel(t, PotentialInviteStatus.Declined),
    },
    {
      id: PotentialInviteStatus.Pending,
      name: getStatusLabel(t, PotentialInviteStatus.Pending),
    },
  ]

  const handleSourceSelection = (
    _: string,
    __: string,
    selectedId: string | number
  ) => {
    setPage(1)
    setSourceFilter(
      selectedId === ALL_SOURCES ? undefined : (selectedId as InvitationSource)
    )
  }

  const handleStatusSelection = (
    _: string,
    __: string,
    selectedId: string | number
  ) => {
    setPage(1)
    setStatusFilter(
      selectedId === ALL_STATUSES
        ? undefined
        : (selectedId as PotentialInviteStatus)
    )
  }

  const handlePaginationChange = (newPagination: PaginationResponse) => {
    // setOrderBy resets the page back to 1, so set it before the real
    // target page to avoid it clobbering an intentional page change.
    setPageSize(newPagination.pageSize)
    setOrderBy(newPagination.orderBy)
    setPage(newPagination.page + 1)
  }

  const actionableTableColumns = getAwaitingDecisionsTableColumns(t)

  const handleSendInvite = (selectedEmail: string) => {
    const inviteData = {
      parentFirstName: modalState?.row.parentFirstName,
      parentLastName: modalState?.row.parentLastName,
      parentEmail: selectedEmail,
      communityId: selectedCommunityKey,
      communityName,
      source: modalState?.row.source,
      potentialInviteKey: modalState?.row.potentialInviteKey,
      parentUserKey: modalState?.row.parentUserKey,
      previousPrograms: modalState?.row.previousPrograms,
    }

    const pathToNavigate =
      modalState?.row.source === InvitationSource.LcdReferral
        ? '/invite-new-family'
        : '/re-invite-family'

    navigate({ pathname: pathToNavigate }, { state: inviteData })
    handleCloseModal()
  }

  const rowActions = getAwaitingDecisionsTableRowActions(t, {
    onSendInvite: handleOpenSendInviteModal,
    onFamilyDetails: handleOpenFamilyDetails,
    onPass: handleOpenPassPotentialInviteModal,
  })

  const sharedDropDownProps = {
    variant: DropDownVariant.SortAndFilter,
    fullWidth: !showOnDesktop,
  }

  const mobileFullWidthFormControlProps = {
    [theme.breakpoints.down('sm')]: { width: '100%' },
  }

  return (
    <section aria-label="awaitingDecisions">
      <SearchAndFilterWrapper aria-label="searchAndFilter">
        <SearchBar
          handleSearch={handleSearch}
          placeholder={t(
            'Communities.AwaitingDecisionsTable.Search.Placeholder',
            'Search'
          )}
        />
        <FilterContainer>
          <SortContainer>
            <DropDown
              id="sourceFilter"
              menuOptions={sourceMenuOptions}
              defaultValue={getSourceLabel(t, sourceFilter)}
              value={getSourceLabel(t, sourceFilter)}
              handleSelection={handleSourceSelection}
              formControlProps={mobileFullWidthFormControlProps}
              {...sharedDropDownProps}
            />
          </SortContainer>
          <SortContainer>
            <DropDown
              id="statusFilter"
              menuOptions={statusMenuOptions}
              defaultValue={getStatusLabel(t, statusFilter)}
              value={getStatusLabel(t, statusFilter)}
              handleSelection={handleStatusSelection}
              formControlProps={mobileFullWidthFormControlProps}
              {...sharedDropDownProps}
            />
          </SortContainer>
        </FilterContainer>
      </SearchAndFilterWrapper>
      <StyledSourceCountsBox aria-label="sourceCounts">
        <Typography
          variant="body2"
          component="span"
          color={theme.palette.textOrIcon.tableHeader}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {`${getSourceLabel(t, InvitationSource.LcdReferral)}: `}
          <Box
            component="span"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.textOrIcon.onBackground,
            }}
          >
            {lcdReferralCount ?? '—'}
          </Box>
        </Typography>
        <Typography
          variant="body2"
          component="span"
          color={theme.palette.textOrIcon.tableHeader}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {`${getSourceLabel(t, InvitationSource.ReturningFamily)}: `}
          <Box
            component="span"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.textOrIcon.onBackground,
            }}
          >
            {returningFamilyCount ?? '—'}
          </Box>
        </Typography>
      </StyledSourceCountsBox>
      <ActionableTable
        isLoading={isLoading}
        columns={actionableTableColumns}
        rows={awaitingDecisionsData}
        rowActions={rowActions}
        rowHeight={70}
        customCssTable={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '.MuiDataGrid-columnSeparator': {
            opacity: 0,
          },
          [theme.breakpoints.down('sm')]: {
            '.MuiDataGrid-main': { overflow: 'auto' },
          },
        }}
        noResultsMessage={t(
          'Communities.AwaitingDecisionsTable.Feature.Not.Available',
          'This feature will be available for the 2027—2028 Academic Year.'
        )}
        pagination={{
          ...pagination,
          page: pagination.page - 1,
        }}
        handlePaginationChange={handlePaginationChange}
      />
      {modalState?.modalType === 'sendInvite' && (
        <SendInviteModal
          isOpen
          familyName={modalState.row.familyName}
          source={modalState.row.source}
          contactEmail={modalState.row.contactEmail}
          crmEmails={modalState.row.crmEmails}
          onClose={handleCloseModal}
          onSend={handleSendInvite}
        />
      )}

      {modalState?.modalType === 'familyDetails' && (
        <FamilyDetailsModal
          isOpen
          onClose={handleCloseModal}
          potentialInvite={modalState.row.potentialInvite}
          source={modalState.row.source}
        />
      )}

      {modalState?.modalType === 'passPotentialInvite' && (
        <PassPotentialInviteModal
          isOpen
          potentialInvite={modalState.row}
          communityKey={selectedCommunityKey as number}
          actorKey={actorKey}
          onClose={handleCloseModal}
          onComplete={triggerFetch}
        />
      )}
    </section>
  )
}

export default CommunityAwaitingDecisionsTable
